import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './TargetManagement.css';
import api from '../../api/axios.js';
import { FiEye, FiEdit, FiSearch, FiChevronDown, FiUsers, FiTarget, FiCheckCircle, FiClock, FiX, FiCalendar } from "react-icons/fi";
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

const TargetManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [dashboardData, setDashboardData] = useState({});
  
  // Filter states
  const [selectedState, setSelectedState] = useState('');
  const [selectedRole, setSelectedRole] = useState('advisor-district-coordinator');
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  
  // Modal states
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [targetLoading, setTargetLoading] = useState(false);
  
  // Target form data
  const [targetForm, setTargetForm] = useState({
    target: '',
    month: '',
    year: new Date().getFullYear(),
    startDate: '',
    endDate: ''
  });
  
  // Edit form data
  const [editForm, setEditForm] = useState({
    name: '',
    status: 'active'
  });
  
  const states = [
    'up', 'maharashtra', 'gujarat', 'rajasthan', 'punjab', 'haryana', 
    'delhi', 'west-bengal', 'tamil-nadu', 'karnataka', 'kerala', 'andhra-pradesh'
  ];

   const STATES = [

    { label: "Andhra Pradesh", value: "andhra pradesh" },
    { label: "Arunachal Pradesh", value: "arunachal pradesh" },
    { label: "Assam", value: "assam" },
    { label: "Bihar", value: "bihar" },
    { label: "Chhattisgarh", value: "chhattisgarh" },
    { label: "Goa", value: "goa" },
    { label: "Gujarat", value: "gujarat" },
    { label: "Haryana", value: "haryana" },
    { label: "Himachal Pradesh", value: "himachal pradesh" },
    { label: "Jharkhand", value: "jharkhand" },
    { label: "Karnataka", value: "karnataka" },
    { label: "Kerala", value: "kerala" },
    { label: "Madhya Pradesh", value: "madhya pradesh" },
    { label: "Maharashtra", value: "maharashtra" },
    { label: "Manipur", value: "manipur" },
    { label: "Meghalaya", value: "meghalaya" },
    { label: "Mizoram", value: "mizoram" },
    { label: "Nagaland", value: "nagaland" },
    { label: "Odisha", value: "odisha" },
    { label: "Punjab", value: "punjab" },
    { label: "Rajasthan", value: "rajasthan" },
    { label: "Sikkim", value: "sikkim" },
    { label: "Tamil Nadu", value: "tamil nadu" },
    { label: "Telangana", value: "telangana" },
    { label: "Tripura", value: "tripura" },
    { label: "Uttar Pradesh", value: "uttar pradesh" },
    { label: "Uttarakhand", value: "uttarakhand" },
    { label: "West Bengal", value: "west bengal" },
    { label: "Delhi", value: "delhi" },
    { label: "Jammu & Kashmir", value: "jammu and kashmir" },
    { label: "Ladakh", value: "ladakh" },
    { label: "Chandigarh", value: "chandigarh" },
    { label: "Puducherry", value: "puducherry" },
    { label: "Andaman & Nicobar", value: "andaman and nicobar" },
    { label: "Dadra & Nagar Haveli", value: "dadra and nagar haveli" },
    { label: "Daman & Diu", value: "daman and diu" },
    { label: "Lakshadweep", value: "lakshadweep" }
  ];
  
  const roles = [
    { value: 'advisor-district-coordinator', label: 'District Coordinator' },
    { value: 'advisor-commisionerate', label: 'Commissionery Manager' },
    { value: 'advisor-state-head', label: 'State Head' }
  ];

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  useEffect(() => {
    fetchDashboardData();
    fetchAgents(1);
  }, [selectedState, selectedRole, debouncedSearch]);

  const fetchDashboardData = async () => {
    try {
      const res = await api.get('/admin/target-dashboard');
      setDashboardData(res.data.data);
    } catch (error) {
      console.error('Fetch dashboard error:', error);
    }
  };

  const fetchAgents = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        roleType: 'customer',
        userType: selectedRole,
        page,
        limit: 10,
        ...(selectedState && { state: selectedState }),
        ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
      };

      const res = await api.get("/admin/all-users", { params });
      setAgents(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch (error) {
      console.error("Fetch agents error:", error);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTargetAssignment = async () => {
    try {
      setTargetLoading(true);
      let endpoint = '';
      let body = {};

      const advisorRole = selectedAgent.roles?.find(role => 
        ['advisor-district-coordinator', 'advisor-commisionerate', 'advisor-state-head'].includes(role.userType)
      );

      if (advisorRole?.userType === 'advisor-state-head') {
        endpoint = '/admin/target/state-head';
        body = {
          stateHeadId: selectedAgent._id,
          target: parseInt(targetForm.target),
          month: targetForm.month,
          startDate: targetForm.startDate,
          endDate: targetForm.endDate,
          year: targetForm.year
        };
      } else if (advisorRole?.userType === 'advisor-commisionerate') {
        endpoint = '/admin/target/commissionerate';
        body = {
          stateHeadId: selectedAgent._id,
          distributions: [{
            target: parseInt(targetForm.target),
            commissionerateId: selectedAgent._id
          }],
          month: targetForm.month,
          startDate: targetForm.startDate,
          endDate: targetForm.endDate,
          year: targetForm.year
        };
      } else if (advisorRole?.userType === 'advisor-district-coordinator') {
        endpoint = '/admin/target/district';
        body = {
          commissionerateId: selectedAgent._id,
          distributions: [{
            target: parseInt(targetForm.target),
            districtCoordinatorId: selectedAgent._id
          }],
          month: targetForm.month,
          startDate: targetForm.startDate,
          endDate: targetForm.endDate,
          year: targetForm.year
        };
      }

      await api.post(endpoint, body);
      toast.success('Target assigned successfully!');
      setShowTargetModal(false);
       setTimeout(() => {
        fetchAgents();
        fetchDashboardData();
      }, 2000);
    } catch (error) {
      console.error('Target assignment error:', error);
      toast.error('Failed to assign target');
    } finally {
      setTargetLoading(false);
    }
  };

  const handleEditAgent = async () => {
    try {
      await api.put(`/user/profile?user_id=${selectedAgent._id}`, {
        name: editForm.name,
        status: editForm.status
      });
      toast.success('Agent updated successfully!');
      setShowEditModal(false);
        setTimeout(() => {
        fetchAgents();
        
      }, 2000);
    } catch (error) {
      console.error('Edit agent error:', error);
      toast.error('Failed to update agent');
    }
  };

  const openTargetModal = (agent) => {
    setSelectedAgent(agent);
    setTargetForm({
      target: '',
      month: '',
      year: new Date().getFullYear(),
      startDate: '',
      endDate: ''
    });
    setShowTargetModal(true);
  };

  const openEditModal = (agent) => {
    setSelectedAgent(agent);
    setEditForm({
      name: agent.name,
      status: agent.status || 'active'
    });
    setShowEditModal(true);
  };

  const formatRoleName = (userType) => {
    const roleMap = {
      'advisor-district-coordinator': 'District Coordinator',
      'advisor-commisionerate': 'Commissionery Manager',
      'advisor-state-head': 'State Head'
    };
    return roleMap[userType] || userType;
  };

  const formatTarget = (target) => {
    if (!target || !target.assigned) return '0';
    return `${target.assigned.toLocaleString()}`;
  };

  return (
    <div className="target-management">
      <div className="target-management-header">
        <h1>Target Management</h1>
      </div>

      {/* Dashboard Stats */}
      <div className="stats-cards">
        <div className="stat-card assigned-stat">
          <div className="stat-info">
            <span className="stat-label">Total Assigned</span>
            <span className="stat-value">{dashboardData.totalAssigned || 0}</span>
          </div>
          <div className="stat-icon assigned-icon">
            <FiTarget />
          </div>
        </div>
        <div className="stat-card completed-stat">
          <div className="stat-info">
            <span className="stat-label">Total Completed</span>
            <span className="stat-value">{dashboardData.totalCompleted || 0}</span>
          </div>
          <div className="stat-icon completed-icon">
            <FiCheckCircle />
          </div>
        </div>
        <div className="stat-card pending-stat">
          <div className="stat-info">
            <span className="stat-label">Total Pending</span>
            <span className="stat-value">{dashboardData.totalPending || 0}</span>
          </div>
          <div className="stat-icon pending-icon">
            <FiClock />
          </div>
        </div>
      </div>

      {/* Main Table Section */}
      <div className="table-section">
        <div className="table-header">
          <h3>🎯 Target Assignment</h3>
          
          <div className="table-actions">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="search-icon"><FiSearch /></span>
            </div>

            <div className="custom-dropdown">
              <button 
                className="dropdown-trigger"
                onClick={() => setStateDropdownOpen(!stateDropdownOpen)}
              >
                {selectedState || 'State'} <FiChevronDown />
              </button>
              {stateDropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-option" onClick={() => {
                    setSelectedState('');
                    setStateDropdownOpen(false);
                  }}>
                    All States
                  </div>
                  {STATES
                    .filter(state =>
                      state.value.toLowerCase().includes(stateSearchTerm.toLowerCase())
                    )
                    .map(state => (
                      <div
                        key={state.value}
                        className="dropdown-option"
                        onClick={() => {
                          setSelectedState(state.value);
                          setStateDropdownOpen(false);
                        }}
                      >
                        {state.label}
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="custom-dropdown">
              <button 
                className="dropdown-trigger"
                onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
              >
                {selectedRole ? roles.find(r => r.value === selectedRole)?.label : 'Role'} <FiChevronDown />
              </button>
              {roleDropdownOpen && (
                <div className="dropdown-menu">
                  {roles.map(role => (
                    <div 
                      key={role.value}
                      className="dropdown-option"
                      onClick={() => {
                        setSelectedRole(role.value);
                        setRoleDropdownOpen(false);
                      }}
                    >
                      {role.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="data-table">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading agents...</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Agent Name</th>
                  <th>Role</th>
                  <th>Target (₹)</th>
                  <th>Phone Number</th>
                  <th>Email</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {agents.length > 0 ? (
                  agents.map((agent, index) => (
                    <tr
                    key={agent._id}
                    className="table-row-animated"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <td>{agent.name ?? 'N/A'}</td>
                  
                    <td>
                      <span className="role-badge">
                        {agent.userType ? formatRoleName(agent.userType) : 'N/A'}
                      </span>
                    </td>
                  
                    <td>{agent.target ? formatTarget(agent.target) : 'N/A'}</td>
                  
                    <td>{agent.mobile_number ?? 'N/A'}</td>
                  
                    <td>{agent.email ?? 'N/A'}</td>
                  
                    <td>
                      <div className="action-icons">
                        <button
                          className="icon-btn view"
                          onClick={() => navigate(`/agent-management/${agent._id}`)}
                          title="View Details"
                        >
                          <FiEye />
                        </button>
                  
                        <button
                          className="icon-btn target"
                          onClick={() => openTargetModal(agent)}
                          title="Assign Target"
                        >
                          <FiTarget />
                        </button>
                  
                        <button
                          className="icon-btn edit"
                          onClick={() => openEditModal(agent)}
                          title="Edit Agent"
                        >
                          <FiEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="no-data">
                      {loading ? 'Loading...' : 'No agents found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Target Assignment Modal */}
      {showTargetModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assign Target - {selectedAgent?.name}</h3>
              <button className="close-btn"  onClick={() => setShowTargetModal(false)}>
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Target Amount</label>
                  <input
                    type="number"
                    value={targetForm.target}
                    onChange={(e) => setTargetForm({...targetForm, target: e.target.value})}
                    placeholder="Enter target amount"
                  />
                </div>
                
                <div className="form-group">
                  <label>Month</label>
                  <select
                    value={targetForm.month}
                    onChange={(e) => setTargetForm({...targetForm, month: parseInt(e.target.value)})}
                  >
                    <option value="">Select Month</option>
                    {months.map(month => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Year</label>
                  <input
                    type="number"
                    value={targetForm.year}
                    onChange={(e) => setTargetForm({...targetForm, year: parseInt(e.target.value)})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={targetForm.startDate}
                    onChange={(e) => setTargetForm({...targetForm, startDate: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={targetForm.endDate}
                    onChange={(e) => setTargetForm({...targetForm, endDate: e.target.value})}
                  />
                </div>

                  <div className="modal-actions">
                <button 
                  className="cancel-btn" 
                  onClick={() => setShowTargetModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="assign-btn"
                  onClick={handleTargetAssignment}
                  disabled={targetLoading}
                >
                  {targetLoading ? 'Assigning...' : 'Assign Target'}
                </button>
              </div>
              </div>
              
            
            </div>
          </div>
        </div>
      )}

      {/* Edit Agent Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Agent - {selectedAgent?.name}</h3>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button 
                  className="cancel-btn" 
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="save-btn"
                  onClick={handleEditAgent}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default TargetManagement;